/**
 * @param {string} label
 * @param {string} path
 * @param {number} order
 * @param {boolean} measurementField
 */
function field(
  label,
  path,
  order,
  measurementField = false,
  { infractionOpts = { infractable: true, fieldNameAlias: null } } = {}
) {
  infractionOpts.fieldNameAlias = infractionOpts.fieldNameAlias || path
  infractionOpts.infractable = infractionOpts.infractable ?? true

  const isRawField = path.startsWith('raw.')

  return {
    label,
    path,
    order,
    isRawField,
    measurementField,
    infractionOpts
  }
}

export const Delivery = {
  raw: {
    deliveryArrivalDate: field('Arrival Date', 'raw.deliveryArrivalDate', 1),
    deliveryArrivalTime: field('Arrival Time', 'raw.deliveryArrivalTime', 2),
    deliveryExitDate: field('Exit Date', 'raw.deliveryExitDate', 3),
    deliveryExitTime: field('Exit Time', 'raw.deliveryExitTime', 4),
    deliveryId: field('Delivery Number', 'raw.deliveryId', 5),
    deliveryAddress: field('Delivery Address', 'raw.deliveryAddress', 6),
    dispatchAddress: field('Dispatch Address', 'raw.dispatchAddress', 7),
    tradeContractor: field('Trade Contractor', 'raw.tradeContractor', 8),
    supplierName: field('Supplier Name', 'raw.supplierName', 9),
    vehicleRegistration: field('Vehicle Reg', 'raw.vehicleRegistration', 10),
    receiverReferenceNumber: field(
      'Receiver Reference',
      'raw.receiverReferenceNumber',
      11
    ),
    supplierReferenceNumber: field(
      'Supplier Reference',
      'raw.supplierReferenceNumber',
      12
    )
  },
  data: {
    deliveryArrivalDate: field('Arrival Date', 'data.deliveryArrivalDate', 1),
    deliveryExitDate: field('Exit Date', 'data.deliveryExitDate', 2),
    deliveryArrivalDateUS: field('Arrival Date', 'data.deliveryArrivalDateUS'),
    deliveryExitDateUS: field('Exit Date', 'data.deliveryExitDateUS'),
    deliveryId: field('Delivery Number', 'data.deliveryId', 3),
    deliveryAddress: field('Delivery Address', 'data.deliveryAddress', 4),
    deliveryAddressKnown: field(
      'Known Delivery Address',
      'data.deliveryAddressKnown',
      5
    ),
    deliveryAddressEntered: field('', 'data.deliveryAddressEntered'),
    deliveryAddressCoordinates: field('', 'data.deliveryAddressCoordinates'),
    givenDispatchAddress: field(
      'Dispatch Address',
      'data.givenDispatchAddress',
      6
    ),
    dispatchAddressKnown: field(
      'Known Dispatch Address',
      'data.dispatchAddressKnown',
      7
    ),
    dispatchAddressEntered: field('', 'data.dispatchAddressEntered'),
    dispatchAddressCoordinates: field('', 'data.dispatchAddressCoordinates'),
    tradeContractor: field('Trade Contractor', 'data.tradeContractor', 8),
    supplierName: field('Given Supplier Name', 'data.supplierName', 9),
    supplierId: field('Known Supplier Company', 'data.supplierId', 10, false, {
      infractionOpts: {
        fieldNameAlias: 'data.knownSupplierName'
      }
    }),
    knownSupplierName: field(
      'Known Supplier Name',
      'data.knownSupplierName',
      undefined,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    ),
    givenSupplierCertifications: field(
      'Given Supplier Certifications',
      'data.givenSupplierCertifications',
      11
    ),
    vehicleRegistration: field('Vehicle Reg', 'data.vehicleRegistration', 12),
    receiverReferenceNumber: field(
      'Receiver Reference',
      'data.receiverReferenceNumber',
      13
    ),
    supplierReferenceNumber: field(
      'Supplier Reference',
      'data.supplierReferenceNumber',
      14
    ),
    knownSupplierCertifications: field(
      'Known Supplier Certifications',
      'data.knownSupplierCertifications',
      undefined,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    ),
    notes: field('Notes', 'data.notes', 15, false, {
      infractionOpts: {
        infractable: false
      }
    })
  },
  movementId: field('Delivery Number', 'data.deliveryId'),
  rawLineItems: {
    id: field('Id', 'id'),
    name: field('Name', 'name', 1),
    quantity: field('Quantity (count)', 'quantity', 2),
    height: field('Height', 'height', 3),
    width: field('Width', 'width', 4),
    length: field('Length', 'length', 5),
    volume: field('Volume', 'volume', 6),
    weight: field('Weight', 'weight', 7),
    productionDate: field('Produced At Date', 'productionDate', 8),
    productionTime: field('Produced At Time', 'productionTime', 9),
    usedAtDate: field('Used At Date', 'usedAtDate', 10),
    usedAtTime: field('Used At Time', 'usedAtTime', 11),
    certifications: field('Product Certifications', 'certifications', 12),
    // Use extra-high numbers for isDeleted
    isDeleted: field('Deleted', 'isDeleted', 101),
    isRemoved: field('Removed', 'isRemoved', 102)
  },
  rawLineItemPrefix: 'raw.products',
  lineItems: {
    id: field('Id', 'id'),
    name: field('Name', 'name', 1),
    quantity: field('Quantity (count)', 'quantity', 2),
    height: field('Height', 'height', 3, true),
    width: field('Width', 'width', 4, true),
    length: field('Length', 'length', 5, true),

    heightUoM: field('Height unit', 'heightUoM'),
    widthUoM: field('Width unit', 'widthUoM'),
    lengthUoM: field('Length unit', 'lengthUoM'),

    calculatedVolume: field('Estimated Volume', 'calculatedVolume', 6, true),
    givenVolume: field('Given Volume', 'givenVolume', 7, true),
    givenVolumeUoM: field('Given Volume Units', 'givenVolumeUoM'),
    estimatedWeight: field('Estimated Weight', 'estimatedWeight', 8, true),
    weight: field('Given Weight', 'weight', 9, true),
    weightUoM: field('Given Weight Units', 'weightUoM'),

    productionTime: field('Produced At', 'productionTime', 10),
    usedAtTime: field('Used At', 'usedAtTime', 11),

    givenCertifications: field(
      'Product Certifications',
      'givenCertifications',
      12
    ),
    knownCertifications: field(
      'Known Product Certifications',
      'knownCertifications',
      null,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    ),
    referenceProductId: field(
      'Reference Product',
      'referenceProductId',
      12,
      false,
      {
        infractionOpts: {
          fieldNameAlias: 'knownProductName'
        }
      }
    ),
    knownProductName: field(
      'Reference Product Name',
      'knownProductName',
      undefined,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    ),
    productGroups: field('Product Group', 'productGroups', 13, false, {
      infractionOpts: {
        infractable: false
      }
    }),
    isHiredOrRented: field('Hired or Rented', 'isHiredOrRented', 14),

    // Use extra-high numbers for isDeleted and isRemoved so the 'order' key is consistant between Delivery and Waste
    isDeleted: field('Deleted', 'isDeleted', 101),
    isRemoved: field('Removed', 'isRemoved', 102)
  },
  lineItemPrefix: 'data.products',
  lineItemNameField: 'name',
  lineItemDisplayNamePlural: 'Product Items'
}

export const DeliveryImperial = {
  lineItems: {
    id: field('Id', 'id'),
    name: field('Name', 'name', 1, true),
    heightYards: field('Yards', 'heightYards', undefined, true),
    heightFeet: field('Feet', 'heightFeet', undefined, true),
    heightInches: field('Inches', 'heightInches', undefined, true),
    widthYards: field('Yards', 'widthYards', undefined, true),
    widthFeet: field('Feet', 'widthFeet', undefined, true),
    widthInches: field('Inches', 'widthInches', undefined, true),
    lengthYards: field('Yards', 'lengthYards', undefined, true),
    lengthFeet: field('Feet', 'lengthFeet', undefined, true),
    lengthInches: field('Inches', 'lengthInches', undefined, true),
    calculatedVolume: field('Estimated Volume', 'calculatedVolume', 6),
    givenVolume: field('Given Volume', 'givenVolume', 7, true),
    givenVolumeUoM: field('Given Volume Units', 'givenVolumeUoM'),
    estimatedWeight: field('Estimated Weight', 'estimatedWeight', 8),
    weight: field('Given Weight', 'weight', 9, true),
    weightUoM: field('Given Weight Units', 'weightUoM'),
    productionTimeUS: field('Produced At', 'productionTimeUS', 10, true),
    usedAtTimeUS: field('Used At', 'usedAtTimeUS', 11, true)
  }
}

export const Waste = {
  raw: {
    removalDate: field('Collection Arrival Date', 'raw.removalDate', 1),
    removalTime: field('Collection Arrival Time', 'raw.removalTime', 2),
    removalExitDate: field('Collection Exit Date', 'raw.removalExitDate', 3),
    removalExitTime: field('Collection Exit Time', 'raw.removalExitTime', 4),
    consignmentId: field('Consignment Number (WTN)', 'raw.consignmentId', 5),
    subcontractorName: field('Subcontractor', 'raw.subcontractorName', 6),
    originSiteAddress: field('Origin Site Address', 'raw.originSiteAddress', 7),
    classificationCode: field('SIC Code', 'raw.classificationCode', 8),
    vehicleRegistration: field('Vehicle Reg', 'raw.vehicleRegistration', 9),
    carrierName: field('Carrier Name', 'raw.carrierName', 10),
    carrierLicenceId: field('Carrier Licence', 'raw.carrierLicenceId', 11),
    facilityOperatorName: field(
      'Facility Operator',
      'raw.facilityOperatorName',
      12
    ),
    facilitySiteAddress: field(
      'Facility Site Address',
      'raw.facilitySiteAddress',
      13
    ),
    facilityPermitId: field('Facility Permit', 'raw.facilityPermitId', 14),
    facilityPermitPresented: field(
      'Permit Presented',
      'raw.facilityPermitPresented',
      15
    ),
    receiverReferenceNumber: field(
      'Sender Reference',
      'raw.receiverReferenceNumber',
      16
    ),
    supplierReferenceNumber: field(
      'Carrier Reference',
      'raw.supplierReferenceNumber',
      17
    )
  },
  data: {
    removalDate: field('Collection Arrival', 'data.removalDate', 1),
    removalDateUS: field('Collection Arrival', 'data.removalDateUS'),
    removalExitDate: field('Collection Exit', 'data.removalExitDate', 2),
    removalExitDateUS: field('Collection Exit', 'data.removalExitDateUS'),
    consignmentId: field('Consignment Number (WTN)', 'data.consignmentId', 3),
    subcontractorName: field('Subcontractor', 'data.subcontractorName', 4),
    originSiteAddress: field(
      'Origin Site Address',
      'data.originSiteAddress',
      5
    ),
    originSiteAddressKnown: field(
      'Known Origin Site Address',
      'data.originSiteAddressKnown',
      6
    ),
    originSiteAddressEntered: field('', 'data.originSiteAddressEntered'),
    originSiteAddressCoordinates: field(
      '',
      'data.originSiteAddressCoordinates'
    ),
    classificationCode: field('SIC Code', 'data.classificationCode', 7),
    vehicleRegistration: field('Vehicle Reg', 'data.vehicleRegistration', 8),
    carrierName: field('Carrier Name', 'data.carrierName', 9),
    carrierLicenceId: field('Carrier Licence', 'data.carrierLicenceId', 10),
    facilityOperatorName: field(
      'Facility Operator',
      'data.facilityOperatorName',
      11
    ),
    facilitySiteAddress: field(
      'Facility Site Address',
      'data.facilitySiteAddress',
      12
    ),
    facilitySiteAddressKnown: field(
      'Known Facility Site Address',
      'data.facilitySiteAddressKnown',
      13
    ),
    facilitySiteAddressEntered: field('', 'data.facilitySiteAddressEntered'),
    facilitySiteAddressCoordinates: field(
      '',
      'data.facilitySiteAddressCoordinates'
    ),
    facilityPermitId: field('Facility Permit', 'data.facilityPermitId', 14),
    facilityPermitPresented: field(
      'Permit Presented',
      'data.facilityPermitPresented',
      15
    ),
    receiverReferenceNumber: field(
      'Sender Reference',
      'data.receiverReferenceNumber',
      16
    ),
    supplierReferenceNumber: field(
      'Carrier Reference',
      'data.supplierReferenceNumber',
      17
    ),
    notes: field('Notes', 'data.notes', 18)
  },
  movementId: field('Consignment Number (WTN)', 'data.consignmentId'),
  rawLineItems: {
    id: field('Id', 'id'),
    containerType: field('Container Type', 'containerType', 1),
    wasteCode: field('Waste Code (EWC)', 'wasteCode', 2),
    volume: field('Volume', 'volume', 3),
    weight: field('Weight', 'weight', 4),
    quantity: field('Quantity', 'quantity', 5),
    // Use extra-high numbers for isDeleted
    isDeleted: field('Deleted', 'isDeleted', 101),
    isRemoved: field('Removed', 'isRemoved', 102)
  },
  rawLineItemPrefix: 'raw.items',
  lineItems: {
    id: field('Id', 'id'),
    containerType: field('Container Type', 'containerType', 1),
    knownContainerType: field('Known Container Type', 'knownContainerType', 2),
    wasteCode: field('Waste Code (EWC)', 'wasteCode', 3),
    volumeEstimated: field('Estimated Volume', 'volumeEstimated', 4, true),
    volume: field('Given Volume', 'volume', 5, true),
    volumeUoM: field('Volume Units', 'volumeUoM'),
    weightEstimated: field('Estimated Weight', 'weightEstimated', 6, true),
    weight: field('Given Weight', 'weight', 7, true),
    weightUoM: field('Given Weight Units', 'weightUoM'),
    quantity: field('Quantity', 'quantity', 8),
    // Use extra-high numbers for isDeleted and isRemoved so the 'order' key is consistant between Delivery and Waste
    isDeleted: field('Deleted', 'isDeleted', 101),
    isRemoved: field('Removed', 'isRemoved', 102)
  },
  lineItemPrefix: 'data.items',
  lineItemNameField: 'containerType',
  lineItemDisplayNamePlural: 'Items'
}

// Note: this is used by unit tests and is here so it can leverage _realistic_ infrastructure and be naturally updated whenever data structures change.
export const FakeModule = {
  data: {
    field1: field('Field 1', 'data.field1'),
    field2: field('Field 2', 'data.field2', undefined, false, {
      infractionOpts: {
        fieldNameAlias: 'data.field2Name'
      }
    }),
    notInfractable1: field(
      'Not Infractable 1',
      'data.notInfractable1',
      undefined,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    )
  },
  movementId: field('Field 1', 'data.field1'),
  lineItems: {
    field3: field('Field 3', 'field3'),
    field4: field('Field 4', 'field4', undefined, false, {
      infractionOpts: {
        fieldNameAlias: 'field4Name'
      }
    }),
    notInfractable2: field(
      'Not Infractable 1',
      'data.notInfractable1',
      undefined,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    )
  },
  lineItemPrefix: 'data.items',
  lineItemNameField: 'field3',
  lineItemDisplayNamePlural: 'Data Items'
}

const all = {
  Delivery,
  delivery: Delivery,
  DeliveryImperial,
  deliveryImperial: DeliveryImperial,
  Waste,
  waste: Waste,
  FakeModule,
  fakemodule: FakeModule
}

/**
 * @returns {Delivery}
 */
export const getFields = moduleName => {
  return all[moduleName.toLowerCase()]
}
